import { createContext, useContext } from 'react';
import type { PaneId } from './paneLayout';

/**
 * **中心区域**视图。
 *
 * 改造后只剩这三个：`chapters`（编辑器，创作主战场）、`ai-chat`（独立的 AI 对话页）、
 * `version-history`（从编辑器工具栏进入，无侧栏入口）。
 *
 * 「设定管理 / 资料库 / 伏笔追踪」**不再是中心视图** —— 它们移到了右侧可同时开启的
 * 窄面板里（见 `paneLayout.ts` 的 `PaneId`）。这样作者写到一半要对照设定时，
 * 正文不必让位：面板在右侧展开，编辑区仍在原处。
 */
export type EditorView = 'chapters' | 'ai-chat' | 'version-history';
export type DataTab = 'factions' | 'characters' | 'items';

/**
 * 侧边栏中**带子列表**（可展开）的视图。
 *
 * 迁移后只剩 `chapters`（章节树）—— 它确实是"在这个工作区里可直接跳转的多个目标"。
 *
 * `ai-chat` **已从这里移除**：会话列表（含「新建对话」）搬进了 AI 对话视图内部。
 * 原先它挂在侧栏的 AI 对话子菜单里，于是"侧栏收起 ⇒ 会话列表不存在 ⇒ 视图空态
 * 连新建对话都点不到"，成了死路（用户实测报出来的）。列表放进视图后，
 * 侧栏那一项退化成**纯切换入口**（叶子，没有子菜单）。
 */
export const SIDEBAR_VIEWS_WITH_CHILDREN: ReadonlySet<EditorView> = new Set<EditorView>([
  'chapters',
]);

export interface EditorContextType {
  activeView: EditorView;
  setActiveView: (view: EditorView) => void;
  expandedMenus: Record<EditorView, boolean>;
  setMenuExpanded: (view: EditorView, expanded: boolean) => void;
  toggleMenu: (view: EditorView) => void;
  // 注：settingsTab 已移除 —— 设定管理只剩「作品设定」单页
  dataTab: DataTab;
  setDataTab: (tab: DataTab) => void;
  /**
   * 把某页**显示出来**（全局搜索命中设定/资料库时用；侧栏筛选态下的入口也用）。
   *
   * 语义是"显示"，不是"来回切"：已在分屏里就什么都不做
   * （不该把用户钉住的东西挪走），否则放进**主面板**。
   * 完整语义见 `paneLayout.ts` 的 `showPane` / `togglePaneVisibility`。
   */
  showPane: (id: PaneId) => void;
  /**
   * 某页此刻是否**已经显示在屏幕上**（占着共享列，或被拆分出来独占一列）。
   *
   * 只读判定，不改布局 —— 与 `Editor/index.tsx` 里渲染 `<section hidden={!isShown}>`
   * 用的是**同一份状态**（`paneLayout.visibleColumns`），所以它说的"可见"
   * 就是用户眼里的可见，不需要去查 DOM。
   *
   * 用途：面板已经在眼前时，就不要再发"请到面板里处理"这类提醒（纯噪音）。
   */
  isPaneVisible: (id: PaneId) => boolean;
}

export const EditorContext = createContext<EditorContextType>({
  activeView: 'chapters',
  setActiveView: () => { /* noop */ },
  expandedMenus: { chapters: true, 'ai-chat': false, 'version-history': false },
  setMenuExpanded: () => { /* noop */ },
  toggleMenu: () => { /* noop */ },
  dataTab: 'factions',
  setDataTab: () => { /* noop */ },
  showPane: () => { /* noop */ },
  // 默认（没有 Provider 时）：右侧面板根本没渲染 → 如实报"不可见"
  isPaneVisible: () => false,
});

export function useEditorContext() {
  return useContext(EditorContext);
}
